import { useState } from "react";
import styles from "./CuisineChooser.module.css";
import { categories } from "./categories";
import { useCart } from "components/cart/cart-provider";
import {
  searchRecipesForCategory,
  detectDietTypesFromQuery,
} from "utils/search-utils";
import DietTypeFilter, { DietType } from "./DietTypeFilter";
import DietPreferenceDisplay from "./DietPreferenceDisplay";

import { validCombinations } from "./validCombinations";

const CuisineChooser = () => {
  const cart = useCart();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedDietTypes, setSelectedDietTypes] = useState<DietType[]>([]);

  // Get detected diet types from search query
  const searchQuery = cart.searchQuery || "";
  const detectedDietTypes = detectDietTypesFromQuery(searchQuery);

  // Helper to check if a category is disabled
  const isCategoryDisabled = (categoryId: string) => {
    const data = validCombinations[categoryId];
    if (!data) return false; // Assume enabled if no data available yet

    // If completely empty
    if ((data.counts["none"] || 0) === 0) return true;

    // If diet types are selected, check if this category has recipes for them
    if (selectedDietTypes.length > 0) {
      const key = [...selectedDietTypes].sort().join("+");
      return (data.counts[key] || 0) === 0;
    }

    return false;
  };

  // Helper to determine disabled diet types based on selected category
  const getDisabledDietTypes = (): DietType[] => {
    if (!selectedCategory) return [];
    
    const data = validCombinations[selectedCategory];
    if (!data) return [];

    const allDietTypes: DietType[] = ["vegan", "glutenfrei", "vegetarisch"];
    
    return allDietTypes.filter(diet => {
      // Always allow unselecting
      if (selectedDietTypes.includes(diet)) return false;

      // Check if adding this diet type results in 0 recipes
      const newCombination = [...selectedDietTypes, diet].sort().join("+");
      return (data.counts[newCombination] || 0) === 0;
    });
  };

  const handleSelectCategory = async (categoryId: string) => {
    // Prevent selecting disabled categories
    if (isCategoryDisabled(categoryId)) return;

    // Toggle selection if clicking the same category again
    if (categoryId === selectedCategory) {
      setSelectedCategory(null);
      cart.setSuggestedRecipes([]);
    } else {
      setSelectedCategory(categoryId);

      // Auto-select diet types if detected from search query
      if (detectedDietTypes.length > 0 && selectedDietTypes.length === 0) {
        setSelectedDietTypes(detectedDietTypes);
      }

      const recipes = await searchRecipesForCategory(categoryId, {
        serviceId: "billa", // Always use Billa for recipe searches
        dietTypes:
          detectedDietTypes.length > 0
            ? detectedDietTypes
            : selectedDietTypes.length > 0
            ? selectedDietTypes
            : undefined,
        query: searchQuery, // Pass the search query for auto-detection
      });
      cart.setSuggestedRecipes(recipes);
    }
  };

  const handleDietTypesChange = async (dietTypes: DietType[]) => {
    setSelectedDietTypes(dietTypes);

    // If a category is selected, refresh recipes with new diet types
    if (selectedCategory) {
      const recipes = await searchRecipesForCategory(selectedCategory, {
        serviceId: "billa",
        dietTypes: dietTypes.length > 0 ? dietTypes : undefined,
      });
      cart.setSuggestedRecipes(recipes);
    }
  };

  const disabledDietTypes = getDisabledDietTypes();

  return (
    <div className={styles.chooserContainer}>
      {/* Diet Preference Display - Fixed at top */}
      <DietPreferenceDisplay
        detectedDietTypes={detectedDietTypes}
        searchQuery={searchQuery}
      />

      {/* Diet Type Filter - Fixed below preference display */}
      <DietTypeFilter
        selectedDietTypes={selectedDietTypes}
        onDietTypesChange={handleDietTypesChange}
        disabledDietTypes={disabledDietTypes}
      />

      {/* Categories - Scrollable section */}
      <div className={styles.categoriesWrapper}>
        {categories.map((category) => {
          const isDisabled = isCategoryDisabled(category.categoryId as string);
          const isSelected = category.categoryId === selectedCategory;
          return (
            <div
              key={category.categoryId}
              className={`${styles.categoryItem} ${
                isSelected ? styles.selected : ""
              } ${isDisabled ? styles.disabled : ""}`}
              onClick={() =>
                handleSelectCategory(category.categoryId as string)
              }
            >
              <div className={styles.imageContainer}>
                <div
                  style={{
                    overflow: "hidden",
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                  }}
                >
                  {category.image ? (
                    <img
                      src={category.image}
                      alt={category.name}
                      className={styles.categoryImage}
                    />
                  ) : (
                    <div className={styles.categoryImagePlaceholder}></div>
                  )}
                </div>

                {/* Render indicator icon conditionally */}
                {isSelected && (
                  <div className={styles.selectedIndicator}>
                    <div className={styles.selectedIndicatorPlaceholder}>✓</div>
                  </div>
                )}
              </div>
              <span className={styles.categoryName}>{category.name}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CuisineChooser;
